<template>
  <nav
    data-track-skip
    class="option-picker"
    :class="{
      'is-upper-case': uppercaseTitles,
      'is-active': showDropdown,
    }"
    @click="toggle"
    @mouseover="show"
    @mouseleave="hide"
  >
    <span
      v-if="currentOption"
      class="option-picker__current_option"
      :class="{
        'option-picker__current_option--no-alternatives': !hasAlternatives,
      }"
      tabindex="0"
      @keydown="onKeydown"
    >
      {{ currentOption.title }}
    </span>
    <a
      v-if="!currentOption && options[0]"
      class="
        option-picker__current_option
        option-picker__current_option--no-selection
      "
      :href="options[0].href"
    >
      {{ options[0].title }}
    </a>
    <DotcomIcon
      v-if="showChevron && hasAlternatives"
      class="option-picker__icon"
      name="chevron-down"
      art="solid"
      :rotation="showDropdown ? 180 : null"
      :color="
        showDropdown || currentOption
          ? '--c-primary-color-2'
          : '--c-primary-neutral-1'
      "
    />
    <Popover v-if="hasAlternatives" :is-visible="showDropdown">
      <ul class="option-picker__option_dropdown">
        <li v-for="(option, index) in popoverOptions" :key="index">
          <a class="option-picker__option" :href="option.href">
            {{ option.title }}
          </a>
        </li>
      </ul>
    </Popover>
  </nav>
</template>

<script>
import Popover from '../common/Popover'

export default {
  name: 'OptionPicker',

  components: {
    DotcomIcon: () => {
      return import(
        /* webpackChunkName: "dotcom-icon" */
        '@/modules/icon/Icon'
      )
    },
    Popover,
  },

  props: {
    options: {
      type: Array,
      required: true,
    },

    uppercaseTitles: Boolean,
    showChevron: Boolean,
  },

  data() {
    return {
      showDropdown: false,
    }
  },

  computed: {
    currentOption() {
      for (let index = 0; index < this.options.length; index++) {
        let option = this.options[index]

        if (option.active) {
          return option
        }
      }
      return null
    },

    hasAlternatives() {
      return this.options.length > 1
    },

    popoverOptions() {
      return this.options.filter((option, index) => {
        const isFirstForNoneSelected = !this.currentOption && index === 0
        const hasTitle = option.title?.length > 0
        return (
          // eslint-disable-next-line no-prototype-builtins
          hasTitle && !option.active && !isFirstForNoneSelected
        )
      })
    },

    smallDesktop() {
      return !this.$mq['lg!']
    },
  },

  methods: {
    hide() {
      this.showDropdown = false
    },
    show() {
      if (!this.smallDesktop) {
        this.showDropdown = true
      }
    },
    toggle() {
      if (this.smallDesktop) {
        this.showDropdown = !this.showDropdown
      }
    },
    onKeydown(event) {
      if (event.keyCode === 13) {
        this.showDropdown = !this.showDropdown
      }
    },
  },
}
</script>

<style lang="postcss" scoped>
.option-picker {
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;

  &__option {
    white-space: nowrap;
  }

  &__current_option {
    /*
      The min-width is a workaround to have a bit of space between the dropdown
      and the window scrollbar when the viewport is smaller than our maximum
      page width. The min-width was a given through the chevron until DOTCOM-164.
    */
    min-width: 26px;
    font-size: 12px;
    font-weight: 600;
    line-height: 20px;
    color: var(--c-primary-color-2);
    white-space: nowrap;
  }

  &.is-upper-case {
    .option-picker__current_option,
    .option-picker__option_dropdown {
      text-transform: uppercase;
    }
  }

  &__current_option--no-selection {
    color: var(--c-primary-neutral-1);

    &:hover {
      color: var(--c-primary-color-2);
    }
  }

  &__current_option--no-alternatives {
    color: var(--c-primary-neutral-1);
    cursor: default;

    &:hover {
      color: var(--c-primary-neutral-1);
    }
  }

  & >>> .dotcom-icon {
    font-size: 14px;
    transition: transform 0.5s ease-in-out;
    margin-left: var(--spacing-1);
  }

  &__icon {
    pointer-events: none;
  }

  &__current_option:hover,
  &__option:hover {
    border-bottom: none;
  }
}
</style>
